import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Briefcase, MapPin, Share2, Store, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { shareOrCopy } from "../../lib/share";
import { QLogo } from "../branding/QLogo";
import { NexaTokensChip } from "../NexaTokensChip";

/**
 * SideDrawer — glassmorphic left-navigation drawer.
 *
 * - 85% width on mobile (<768px), 320px fixed on desktop.
 * - Hardware-accelerated transform: translateX() → no layout thrash.
 * - Escape / overlay click / X button all close it.
 * - Body scroll locked while open.
 * - Accessible: role="dialog" + aria-modal + aria-labelledby.
 * - Fallback background if backdrop-filter isn't supported.
 * - Only icons for "Rincón Latino" — NO real GPS/maps (privacy rule).
 */

const NAV_ITEMS = [
  {
    id: "negocios",
    label: "Negocios",
    path: "/negocios",
    icon: Briefcase,
    testId: "drawer-item-negocios",
    hint: "Panel B2B",
  },
  {
    id: "market",
    label: "Market",
    path: "/market",
    icon: Store,
    testId: "drawer-item-market",
    hint: "Tienda de HoloStickers",
  },
  {
    id: "rincon",
    label: "Rincón Latino",
    path: "/rincon-latino",
    icon: MapPin,
    testId: "drawer-item-rincon",
    hint: "Directorio latino",
  },
];

export function SideDrawer({ open, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const onSharePulse = () => {
      setPulse(false);
      setTimeout(() => setPulse(true), 20);
      setTimeout(() => setPulse(false), 1400);
    };
    const onTokensUpdate = (e) => {
      const delta = e?.detail?.delta;
      if (typeof delta === "number" && delta > 0) onSharePulse();
    };
    window.addEventListener("queonda.tokens.pulse", onSharePulse);
    window.addEventListener("queonda.tokens.update", onTokensUpdate);
    return () => {
      window.removeEventListener("queonda.tokens.pulse", onSharePulse);
      window.removeEventListener("queonda.tokens.update", onTokensUpdate);
    };
  }, []);

  const activeId = useMemo(() => {
    const path = location.pathname;
    const match = NAV_ITEMS.find((item) => path === item.path || path.startsWith(item.path + "/"));
    return match?.id || null;
  }, [location.pathname]);

  // Lock body scroll while open + close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the close button (accessibility)
    const focusTimeout = setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 60);
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      clearTimeout(focusTimeout);
      window.removeEventListener("keydown", onKey);
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === "function") {
        try {
          previousFocusRef.current.focus();
        } catch (_e) {
          /* ignore */
        }
      }
    };
  }, [open, onClose]);

  const handleNavigate = (path) => {
    // Close drawer on navigation for a clean transition.
    onClose?.();
    // Slight delay so the drawer close animation feels connected.
    setTimeout(() => navigate(path), 60);
  };

  const handleShare = async () => {
    onClose?.();
    const tag = user?.ondatag ? `@${user.ondatag}` : "@ondafan";
    const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
    // Slight delay so the drawer close animation feels connected before the
    // native share sheet / clipboard toast appear.
    setTimeout(() => {
      shareOrCopy({
        title: "Qué Onda Chat",
        text: `¡Únete a Qué Onda Chat! Encuéntrame como ${tag}`,
        url: shareUrl,
        ref: user?.ondatag || undefined,
      });
    }, 80);
  };

  return (
    <div
      className="side-drawer-root"
      aria-hidden={open ? "false" : "true"}
      data-testid="side-drawer"
      data-open={open ? "true" : "false"}
    >
      {/* Semi-transparent overlay (fades in/out) */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Cerrar navegación"
        tabIndex={open ? 0 : -1}
        data-testid="drawer-overlay"
        className="side-drawer-overlay"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(3, 8, 18, 0.55)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          opacity: open ? 1 : 0,
          transition: "opacity 250ms ease-in-out",
          pointerEvents: open ? "auto" : "none",
          zIndex: 90,
          border: 0,
          padding: 0,
          cursor: open ? "pointer" : "default",
        }}
      />

      {/* Sliding panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="side-drawer-title"
        className="side-drawer-panel"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingLeft: "env(safe-area-inset-left, 0px)",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 250ms ease-in-out",
          zIndex: 91,
          willChange: "transform",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          className="flex items-center justify-between gap-3 px-5 pt-5 pb-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <QLogo size={32} />
            <div className="flex flex-col leading-tight min-w-0">
              <h2
                id="side-drawer-title"
                className="font-display font-bold text-lg tracking-tight truncate"
                style={{ color: "#e6f9ff" }}
              >
                <span className="notranslate" translate="no">Qué Onda Navigation</span>
              </h2>
              <span className="text-[10px] uppercase tracking-[0.22em] font-mono text-slate-500 mt-0.5">
                menú lateral
              </span>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Cerrar navegación"
            data-testid="drawer-close"
            className="shrink-0 p-2 rounded-xl transition-colors"
            style={{
              color: "#cfeeff",
              border: "1px solid rgba(0, 240, 255, 0.25)",
              background: "rgba(0, 240, 255, 0.06)",
            }}
          >
            <X size={20} />
          </button>
        </header>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1.5" aria-label="Navegación principal">
          {user && (
            <div className="px-2 pb-3">
              <NexaTokensChip
                balance={user.nexatokens_balance || 0}
                pulse={pulse}
                size="lg"
              />
            </div>
          )}
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeId === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavigate(item.path)}
                data-testid={item.testId}
                data-active={isActive ? "true" : "false"}
                aria-current={isActive ? "page" : undefined}
                className="side-drawer-item w-full text-left flex items-center gap-3 px-3 py-3 rounded-2xl transition-all"
                style={{
                  border: isActive
                    ? "1px solid rgba(0, 240, 255, 0.65)"
                    : "1px solid rgba(255, 255, 255, 0.06)",
                  background: isActive
                    ? "linear-gradient(135deg, rgba(0,240,255,0.16), rgba(0,240,255,0.04))"
                    : "rgba(255,255,255,0.02)",
                  boxShadow: isActive
                    ? "0 0 0 1px rgba(0,240,255,0.35) inset, 0 0 22px rgba(0,240,255,0.25)"
                    : "none",
                  color: isActive ? "#00f0ff" : "#d7e6f5",
                }}
              >
                <span
                  className="shrink-0 h-9 w-9 flex items-center justify-center rounded-xl"
                  style={{
                    background: isActive
                      ? "rgba(0,240,255,0.16)"
                      : "rgba(255,255,255,0.04)",
                    border: isActive
                      ? "1px solid rgba(0,240,255,0.45)"
                      : "1px solid rgba(255,255,255,0.06)",
                    boxShadow: isActive ? "0 0 12px rgba(0,240,255,0.35)" : "none",
                  }}
                  aria-hidden="true"
                >
                  <Icon size={18} strokeWidth={2.3} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-sm">{item.label}</span>
                  <span
                    className="block text-[11px] mt-0.5"
                    style={{ color: isActive ? "rgba(0,240,255,0.8)" : "rgba(215,230,245,0.55)" }}
                  >
                    {item.hint}
                  </span>
                </span>
              </button>
            );
          })}

          {/* Divider */}
          <div
            className="mx-2 my-3 h-px"
            style={{ background: "rgba(255,255,255,0.08)" }}
            aria-hidden="true"
          />

          {/* Share button */}
          <button
            type="button"
            onClick={handleShare}
            data-testid="drawer-share"
            className="side-drawer-item w-full text-left flex items-center gap-3 px-3 py-3 rounded-2xl transition-all"
            style={{
              border: "1px solid rgba(255, 255, 255, 0.06)",
              background: "rgba(255,255,255,0.02)",
              color: "#d7e6f5",
            }}
          >
            <span
              className="shrink-0 h-9 w-9 flex items-center justify-center rounded-xl"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
              aria-hidden="true"
            >
              <Share2 size={18} strokeWidth={2.3} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-sm">
                Compartir <span className="notranslate" translate="no">Qué Onda</span>
              </span>
              <span
                className="block text-[11px] mt-0.5"
                style={{ color: "rgba(215,230,245,0.55)" }}
              >
                Invita a alguien con tu @tag
              </span>
            </span>
          </button>
        </nav>

        <footer
          className="px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-mono"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            color: "rgba(215,230,245,0.4)",
          }}
        >
          <span className="notranslate" translate="no">Qué Onda Chat</span> · v1
        </footer>
      </aside>

      <style>{`
        .side-drawer-panel {
          width: 85%;
          max-width: 400px;
          background: rgba(13, 21, 32, 0.72);
          backdrop-filter: blur(12px) saturate(150%);
          -webkit-backdrop-filter: blur(12px) saturate(150%);
          border-right: 1px solid rgba(0, 240, 255, 0.14);
          box-shadow: 0 0 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(0, 240, 255, 0.08);
          color: #e6f9ff;
        }
        @media (min-width: 768px) {
          .side-drawer-panel {
            width: 320px;
            max-width: 320px;
          }
        }
        /* Fallback for browsers without backdrop-filter support */
        @supports not ((backdrop-filter: blur(12px)) or (-webkit-backdrop-filter: blur(12px))) {
          .side-drawer-panel {
            background: rgba(13, 21, 32, 0.95);
          }
        }
        .side-drawer-item:hover {
          border-color: rgba(0, 240, 255, 0.35) !important;
          background: rgba(0, 240, 255, 0.05) !important;
        }
        .side-drawer-item:focus-visible {
          outline: 2px solid #00f0ff;
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
}
